import { useState } from 'react';
import type { PaymentMode } from '@dunea/shared';
import {
  apiEnableCashOnDelivery,
  apiSaveRazorpayKeys,
  apiSaveUpiAccount,
  apiStartStripeConnect,
  type PaymentsOverview,
} from '~/lib/api/payments';
import { classNames } from '~/utils/classNames';
import { accountState, accountStateMeta, apiErrorMessage, findAccount, isValidUpiId } from './payments-model';
import { BTN_GHOST, BTN_SECONDARY, CARD, CAPTION, CopyField, ErrorBanner, Field, Pill, Section, Spinner } from './ui';

interface Props {
  appId: string;
  overview: PaymentsOverview;
  onChanged: () => void;
}

export function AccountsSection({ appId, overview, onChanged }: Props) {
  const currency = overview.settings.currency.toUpperCase();

  return (
    <Section title="Accounts" icon="i-ph:bank">
      {currency === 'INR' && <UpiCard appId={appId} overview={overview} onChanged={onChanged} />}
      <StripeCard appId={appId} overview={overview} currency={currency} />
      <RazorpayCard appId={appId} overview={overview} currency={currency} onChanged={onChanged} />
      {currency !== 'INR' && <UpiCard appId={appId} overview={overview} onChanged={onChanged} />}
      <CodCard appId={appId} overview={overview} onChanged={onChanged} />
    </Section>
  );
}

function ProviderHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-white/[0.04] ring-1 ring-inset ring-white/[0.06]">
        <div className={classNames(icon, 'text-base text-secondary')} />
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-primary">{title}</div>
        <div className="text-[11px] text-muted">{subtitle}</div>
      </div>
    </div>
  );
}

function ModeRow({ mode, children }: { mode: PaymentMode; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[12px] text-secondary">{mode === 'live' ? 'Live' : 'Test'}</span>
      <div className="flex min-w-0 items-center gap-2">{children}</div>
    </div>
  );
}

// ─── Stripe (Connect Express, international) ───

function StripeCard({ appId, overview, currency }: { appId: string; overview: PaymentsOverview; currency: string }) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = findAccount(overview.accounts, 'stripe', 'live');
  const test = findAccount(overview.accounts, 'stripe', 'test');
  const liveState = accountState(live);
  const liveMeta = accountStateMeta(liveState);

  const connect = async () => {
    setConnecting(true);
    setError(null);

    try {
      // Stripe sends the owner back here; the overview re-reads the account status on load.
      const here = window.location.href;
      const { url } = await apiStartStripeConnect(appId, 'live', { returnUrl: here, refreshUrl: here });
      window.location.assign(url);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start Stripe onboarding. Please try again.'));
      setConnecting(false);
    }
  };

  return (
    <div className={classNames(CARD, 'p-4')}>
      <ProviderHeader
        icon="i-ph:stripe-logo"
        title="Stripe"
        subtitle="Cards, Apple Pay and Google Pay — international"
      />

      {currency === 'INR' && (
        <p className="mt-3 text-[11px] leading-relaxed text-warning">
          Stripe doesn&rsquo;t take INR payments for India-based sellers. Use Razorpay for INR.
        </p>
      )}

      <div className="mt-3 divide-y divide-hairline border-t border-hairline">
        <ModeRow mode="test">
          {test ? (
            <Pill {...accountStateMeta(accountState(test))} />
          ) : overview.sandbox.stripe ? (
            <span className="text-right text-[11px] text-muted">Works with Dunea&rsquo;s sandbox — no setup</span>
          ) : (
            <span className="text-right text-[11px] text-muted">Not available on this server</span>
          )}
        </ModeRow>
        <ModeRow mode="live">
          <Pill tone={liveMeta.tone} label={liveMeta.label} />
        </ModeRow>
      </div>

      {liveState !== 'active' && (
        <div className="mt-3 space-y-2">
          {liveState === 'restricted' && (
            <p className="text-[11px] leading-relaxed text-muted">
              Stripe needs more details before this account can take payments again.
            </p>
          )}
          <button onClick={connect} disabled={connecting} className={BTN_SECONDARY}>
            {connecting ? <Spinner /> : <div className="i-ph:link text-[13px]" />}
            {liveState === 'none' ? 'Connect Stripe' : 'Continue Stripe setup'}
          </button>
        </div>
      )}

      {error && (
        <div className="mt-3">
          <ErrorBanner message={error} />
        </div>
      )}
    </div>
  );
}

// ─── Razorpay (owner's own keys, INR) ───

function RazorpayCard({
  appId,
  overview,
  currency,
  onChanged,
}: {
  appId: string;
  overview: PaymentsOverview;
  currency: string;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState<PaymentMode | null>(null);

  return (
    <div className={classNames(CARD, 'p-4')}>
      <ProviderHeader icon="i-ph:currency-inr" title="Razorpay" subtitle="UPI, cards, netbanking and wallets — India" />

      {currency !== 'INR' && (
        <p className="mt-3 text-[11px] leading-relaxed text-muted">
          Razorpay only takes INR. Your currency is {currency}.
        </p>
      )}

      <div className="mt-3 divide-y divide-hairline border-t border-hairline">
        {(['test', 'live'] as const).map((mode) => {
          const account = findAccount(overview.accounts, 'razorpay', mode);
          const meta = accountStateMeta(accountState(account));

          return (
            <div key={mode} className="py-1.5">
              <ModeRow mode={mode}>
                {account?.fingerprint && (
                  <span className="truncate font-geist-mono text-[11px] text-muted" title="Saved key">
                    {account.fingerprint}
                  </span>
                )}
                {account ? (
                  <Pill tone={meta.tone} label={meta.label} />
                ) : mode === 'test' && overview.sandbox.razorpay ? (
                  <span className="text-[11px] text-muted">Dunea sandbox</span>
                ) : (
                  <Pill tone={meta.tone} label={meta.label} />
                )}
                <button
                  onClick={() => setOpen(open === mode ? null : mode)}
                  className="text-[11px] font-medium text-secondary underline-offset-2 hover:text-primary hover:underline"
                >
                  {open === mode ? 'Close' : account ? 'Replace keys' : `Add ${mode} keys`}
                </button>
              </ModeRow>

              {open === mode && (
                <RazorpayKeysForm
                  appId={appId}
                  mode={mode}
                  webhookUrl={overview.webhookUrls.razorpay[mode]}
                  onSaved={() => {
                    setOpen(null);
                    onChanged();
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RazorpayKeysForm({
  appId,
  mode,
  webhookUrl,
  onSaved,
}: {
  appId: string;
  mode: PaymentMode;
  webhookUrl: string;
  onSaved: () => void;
}) {
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prefix = mode === 'live' ? 'rzp_live_' : 'rzp_test_';
  const complete = keyId.trim() && keySecret.trim() && webhookSecret.trim();

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!complete) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await apiSaveRazorpayKeys(appId, mode, { keyId: keyId.trim(), keySecret, webhookSecret });
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save these keys. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="mt-2 space-y-3 rounded-lg border border-hairline bg-surface p-3">
      <p className="text-[11px] leading-relaxed text-muted">
        Razorpay Dashboard → Account &amp; Settings → API Keys. {mode === 'live' ? 'Live' : 'Test'} key IDs start with{' '}
        <span className="font-geist-mono text-secondary">{prefix}</span>.
      </p>

      <Field label="Key ID">
        <input
          value={keyId}
          onChange={(e) => setKeyId(e.target.value)}
          placeholder={`${prefix}…`}
          autoComplete="off"
          spellCheck={false}
          className="ui-input-mono"
        />
      </Field>
      <Field label="Key secret">
        <input
          type="password"
          value={keySecret}
          onChange={(e) => setKeySecret(e.target.value)}
          autoComplete="new-password"
          className="ui-input-mono"
        />
      </Field>

      <div className="space-y-1.5">
        <span className={CAPTION}>Webhook</span>
        <p className="text-[11px] leading-relaxed text-muted">
          In Razorpay → Webhooks, add this URL with the{' '}
          <span className="font-geist-mono text-secondary">order.paid</span>,{' '}
          <span className="font-geist-mono text-secondary">payment.failed</span>,{' '}
          <span className="font-geist-mono text-secondary">refund.processed</span> and{' '}
          <span className="font-geist-mono text-secondary">payment.dispute.created</span> events, then paste the secret
          you chose below.
        </p>
        <CopyField value={webhookUrl} />
      </div>

      <Field label="Webhook secret">
        <input
          type="password"
          value={webhookSecret}
          onChange={(e) => setWebhookSecret(e.target.value)}
          autoComplete="new-password"
          className="ui-input-mono"
        />
      </Field>

      {error && <ErrorBanner message={error} />}

      <button type="submit" disabled={saving || !complete} className={BTN_SECONDARY}>
        {saving && <Spinner />}
        {saving ? 'Checking keys…' : `Save ${mode} keys`}
      </button>
    </form>
  );
}

// ─── Cash on delivery (owner confirms by hand) ───

function CodCard({ appId, overview, onChanged }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enabled = overview.accounts.some((a) => a.provider === 'cod' && a.status === 'active');

  const enable = async () => {
    setSaving(true);
    setError(null);

    try {
      await apiEnableCashOnDelivery(appId);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not enable cash on delivery.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classNames(CARD, 'p-4')}>
      <div className="flex items-start justify-between gap-3">
        <ProviderHeader icon="i-ph:hand-coins" title="Cash on delivery" subtitle="Buyers pay when the order arrives" />
        {enabled ? (
          <Pill tone="success" label="Enabled" />
        ) : (
          <button onClick={enable} disabled={saving} className={classNames(BTN_SECONDARY, 'shrink-0')}>
            {saving && <Spinner />}
            Enable cash on delivery
          </button>
        )}
      </div>
      {enabled && (
        <p className="mt-3 text-[11px] leading-relaxed text-muted">
          Cash orders stay pending until you mark them as paid below.
        </p>
      )}
      {error && (
        <div className="mt-3">
          <ErrorBanner message={error} />
        </div>
      )}
    </div>
  );
}

// ─── Direct UPI (owner's own UPI ID, INR, no gateway) ───

function UpiCard({ appId, overview, onChanged }: Props) {
  const current = findAccount(overview.accounts, 'upi', 'live')?.upi;
  const [editing, setEditing] = useState(!current);
  const [vpa, setVpa] = useState(current?.vpa ?? '');
  const [payeeName, setPayeeName] = useState(current?.payeeName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inr = overview.settings.currency.toUpperCase() === 'INR';
  const complete = isValidUpiId(vpa) && payeeName.trim().length >= 2;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      await apiSaveUpiAccount(appId, { vpa: vpa.trim(), payeeName: payeeName.trim() });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this UPI ID.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classNames(CARD, 'p-4')}>
      <div className="flex items-start justify-between gap-3">
        <ProviderHeader
          icon="i-ph:qr-code"
          title="UPI, straight to you"
          subtitle="Google Pay, PhonePe, Paytm. No gateway, no fees"
        />
        {current && !editing && <Pill tone="success" label="On" />}
      </div>

      {!inr && (
        <p className="mt-3 text-[11px] leading-relaxed text-warning">
          UPI takes INR only. Switch the shop currency to INR to offer it.
        </p>
      )}

      {current && !editing ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-3 text-[12px]">
            <span className="min-w-0 truncate font-geist-mono text-primary">{current.vpa}</span>
            <button onClick={() => setEditing(true)} className={BTN_SECONDARY}>
              Change
            </button>
          </div>
          <p className="text-[11px] leading-relaxed text-muted">
            Buyers see &ldquo;{current.payeeName}&rdquo;, pay from any UPI app, then enter the UTR. We email you to
            check it; mark it as paid below once it shows in your UPI app.
          </p>
        </div>
      ) : (
        <form onSubmit={save} className="mt-3 space-y-3">
          <Field
            label="Your UPI ID"
            hint="A business UPI ID (PhonePe Business, Paytm Business, Google Pay for Business) is free and works with every app."
          >
            <input
              value={vpa}
              onChange={(e) => setVpa(e.target.value)}
              placeholder="yourshop@okaxis"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={vpa.trim() !== '' && !isValidUpiId(vpa)}
              className="ui-input-mono"
            />
          </Field>
          {vpa.trim() !== '' && !isValidUpiId(vpa) && (
            <p className="-mt-1 text-[11px] leading-relaxed text-danger">
              That doesn&rsquo;t look like a UPI ID. It looks like name@bank, e.g. yourshop@okaxis.
            </p>
          )}
          <Field label="Name buyers see when they pay">
            <input
              value={payeeName}
              onChange={(e) => setPayeeName(e.target.value)}
              placeholder="Asha Bakes"
              maxLength={50}
              className="ui-input"
            />
          </Field>
          <div className="flex items-center gap-2">
            <button type="submit" disabled={saving || !complete} className={BTN_SECONDARY}>
              {saving && <Spinner />}
              {current ? 'Save UPI ID' : 'Turn on UPI'}
            </button>
            {current && (
              <button type="button" onClick={() => setEditing(false)} className={BTN_GHOST}>
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {error && (
        <div className="mt-3">
          <ErrorBanner message={error} />
        </div>
      )}
    </div>
  );
}
